import { apiSlice } from '../apiSlice';
import {
	ClickPageDTO,
	ClockPageFilter,
} from '../DTO/app-setting/clock.interface';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	AddRequest,
	AddSingleRequest,
	MeditationPageDTO,
	MeditationPageFilter,
	MeditationSinglePageDTO,
	MeditationSinglePageFilter,
	UnitSeriesDetailResDTO,
	UnitSeriesPublishDTO,
	UnitSeriesSinglePageResDTO,
	UnitSingleDetailResDTO,
	UnitSinglePublishDTO,
} from '../DTO/exercise/meditation.interface';

export const bodyApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getBodyList: builder.query<
			SuccessResponse<Paginated<MeditationPageDTO>>,
			MeditationPageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['BodyList'],
		}),
		getBodySortList: builder.query<SuccessResponse<MeditationPageDTO>, null>({
			query: () => ({
				url: `/web/exercise/unitBodyMind/series/sort`,
				method: 'GET',
			}),
			providesTags: ['BodySortList'],
		}),
		setBodySort: builder.mutation<
			SuccessResponse<any>,
			{ seriesIdList: string[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodyList', 'BodySortList'],
		}),
		getBodyPublishingList: builder.query<
			SuccessResponse<MeditationPageDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/exercise/unitBodyMind/series/published`,
				method: 'GET',
			}),
		}),
		setBodyStatus: builder.mutation<
			SuccessResponse<any>,
			{ seriesPublishDTOList: UnitSeriesPublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodyList', 'BodyDetail'],
		}),
		getBodyDetail: builder.query<
			SuccessResponse<UnitSeriesDetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitBodyMind/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['BodyDetail'],
		}),
		addBody: builder.mutation<SuccessResponse<any>, AddRequest>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['BodyList'],
		}),
		updateBody: builder.mutation<SuccessResponse<any>, AddRequest>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodyDetail'],
		}),
		deleteBody: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/unitBodyMind/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getBodySingleList: builder.query<
			SuccessResponse<Paginated<MeditationSinglePageDTO>>,
			MeditationSinglePageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['BodySingleList'],
		}),
		addBodySingle: builder.mutation<SuccessResponse<any>, AddSingleRequest>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/${body.seriesId}`,
				method: 'POST',
				body,
			}),
		}),
		updateBodySingle: builder.mutation<SuccessResponse<any>, AddSingleRequest>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/single/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodySingleDetail'],
		}),
		getBodySingleDetail: builder.query<
			SuccessResponse<UnitSingleDetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitBodyMind/single/${id}`,
				method: 'GET',
			}),
			providesTags: ['BodySingleDetail'],
		}),
		getBodySingleStatusList: builder.query<
			SuccessResponse<UnitSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitBodyMind/series/${id}/published`,
				method: 'GET',
			}),
		}),
		setBodySingleStatus: builder.mutation<
			SuccessResponse<any>,
			{ singlePublishDTOList: UnitSinglePublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/${body.id}/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodySingleList', 'BodySingleDetail'],
		}),
		getBodySingleSortList: builder.query<
			SuccessResponse<UnitSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitBodyMind/series/${id}/sort`,
				method: 'GET',
			}),
			providesTags: ['BodySingleSortList'],
		}),
		setBodySingleSort: builder.mutation<
			SuccessResponse<any>,
			{ singleIdList: string[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/${body.id}/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodySingleSortList', 'BodySingleList'],
		}),
		deleteBodySingle: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/unitBodyMind/single/${id}`,
				method: 'DELETE',
			}),
		}),
		batchUpdateBodyHasNew: builder.mutation<
			SuccessResponse<any>,
			{ list: any[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/has-new`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodyList'],
		}),
		batchUpdateSingleBodyHasNew: builder.mutation<
			SuccessResponse<any>,
			{ id: string; list: any[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitBodyMind/series/${body.id}/has-new`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['BodySingleList'],
		}),
	}),
});

export const {
	useGetBodyListQuery,
	useGetBodySortListQuery,
	useGetBodyPublishingListQuery,
	useSetBodyStatusMutation,
	useGetBodyDetailQuery,
	useAddBodyMutation,
	useUpdateBodyMutation,
	useDeleteBodyMutation,
	useGetBodySingleListQuery,
	useAddBodySingleMutation,
	useUpdateBodySingleMutation,
	useGetBodySingleDetailQuery,
	useGetBodySingleStatusListQuery,
	useSetBodySingleStatusMutation,
	useSetBodySortMutation,
	useGetBodySingleSortListQuery,
	useSetBodySingleSortMutation,
	useDeleteBodySingleMutation,
	useBatchUpdateBodyHasNewMutation,
	useBatchUpdateSingleBodyHasNewMutation,
} = bodyApiSlice;
